import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DCWMPStep4ConstructionPhase = ({ project, onComplete, onBack }) => {
  const [formData, setFormData] = useState({
    construction_type: 'commercial',
    structural_system: 'concrete',
    has_basement: false,
    basement_footprint: '',
    excavation_depth: '',
    onsite_reuse_percentage: 0,
    imported_fill: false,
    construction_rate_override: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const constructionTypes = [
    { value: 'residential_lowrise', label: 'Residential (Low-rise)', rate: 0.08 },
    { value: 'residential_midrise', label: 'Residential (Mid-rise)', rate: 0.10 },
    { value: 'commercial', label: 'Commercial', rate: 0.12 },
    { value: 'mixed_use', label: 'Mixed-use', rate: 0.11 }
  ];

  const structuralSystems = [
    { value: 'concrete', label: 'Concrete - primarily using in-situ or precast concrete elements such as slabs, walls, columns and footings' },
    { value: 'steel', label: 'Steel - primarily utilising structural steel framing and associated metal components' },
    { value: 'timber', label: 'Timber - predominantly incorporating timber framing and engineered timber products for structural elements' }
  ];

  useEffect(() => {
    if (project?.dcwmp_step4) {
      const step4 = project.dcwmp_step4;
      setFormData({
        construction_type: step4.construction_type || 'commercial',
        structural_system: step4.structural_system || 'concrete',
        has_basement: step4.has_basement || false,
        basement_footprint: step4.basement_footprint || '',
        excavation_depth: step4.excavation_depth || '',
        onsite_reuse_percentage: step4.onsite_reuse_percentage || 0,
        imported_fill: step4.imported_fill || false,
        construction_rate_override: step4.construction_rate_override || ''
      });
    }
  }, [project]);

  const getTotalGFA = () => {
    return project?.dcwmp_step2?.total_construction_gfa || 0;
  };

  const getSelectedRate = () => {
    const selected = constructionTypes.find(t => t.value === formData.construction_type);
    return formData.construction_rate_override || selected?.rate || 0.12;
  };

  const getEstimatedWaste = () => {
    return getTotalGFA() * getSelectedRate();
  };

  const getExcavationVolume = () => {
    if (!formData.has_basement) return 0;
    const footprint = parseFloat(formData.basement_footprint) || 0;
    const depth = parseFloat(formData.excavation_depth) || 0;
    return footprint * depth;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('opwmp_token');
      
      const payload = {
        construction_type: formData.construction_type,
        structural_system: formData.structural_system,
        has_basement: formData.has_basement,
        basement_footprint: formData.has_basement ? parseFloat(formData.basement_footprint) || 0 : null,
        excavation_depth: formData.has_basement ? parseFloat(formData.excavation_depth) || 0 : null,
        onsite_reuse_percentage: formData.has_basement ? formData.onsite_reuse_percentage : 0,
        imported_fill: formData.imported_fill,
        construction_rate_override: formData.construction_rate_override ? parseFloat(formData.construction_rate_override) : null
      };

      await axios.put(
        `${API}/api/projects/${project.id}/dcwmp/step4`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-white/30 rounded-md text-sm font-medium text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-white">Construction Phase</h2>
        <p className="text-green-100 mt-1">Define the construction parameters and excavation details</p>
      </div>

      <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Construction Type & Structural System */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Construction Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.construction_type}
              onChange={(e) => setFormData(prev => ({ ...prev, construction_type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            >
              {constructionTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label} (Rate: {type.rate} m³/m²)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Structural System <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.structural_system}
              onChange={(e) => setFormData(prev => ({ ...prev, structural_system: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            >
              {structuralSystems.map(sys => (
                <option key={sys.value} value={sys.value}>{sys.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Estimated Construction Waste */}
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <h3 className="font-medium text-green-800 mb-3">Estimated Construction Waste</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm text-gray-600">Total GFA</p>
              <p className="text-xl font-bold text-green-700">{getTotalGFA().toLocaleString()} m²</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Rate</p>
              <p className="text-xl font-bold text-green-700">{getSelectedRate()} m³/m²</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Est. Waste</p>
              <p className="text-xl font-bold text-green-700">{getEstimatedWaste().toLocaleString()} m³</p>
            </div>
          </div>
        </div>

        {/* Basement Excavation */}
        <div className="border-t pt-6">
          <h3 className="font-medium text-gray-900 mb-4">Basement Excavation</h3>
          
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.has_basement}
                onChange={(e) => setFormData(prev => ({ ...prev, has_basement: e.target.checked }))}
                className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <span className="ml-3 text-sm font-medium text-gray-700">
                This project includes basement excavation
              </span>
            </label>
          </div>

          {formData.has_basement && (
            <div className="space-y-4 pl-4 border-l-4 border-green-200">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Basement Footprint (m²)
                  </label>
                  <input
                    type="number"
                    value={formData.basement_footprint}
                    onChange={(e) => setFormData(prev => ({ ...prev, basement_footprint: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                    placeholder="e.g., 500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Excavation Depth (metres)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.excavation_depth}
                    onChange={(e) => setFormData(prev => ({ ...prev, excavation_depth: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                    placeholder="e.g., 3.5"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  On-site Reuse Percentage: {formData.onsite_reuse_percentage}%
                </label>
                <input
                  type="range"
                  value={formData.onsite_reuse_percentage}
                  onChange={(e) => setFormData(prev => ({ ...prev, onsite_reuse_percentage: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  min="0"
                  max="100"
                  step="5"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0% (All exported)</span>
                  <span>100% (All reused on-site)</span>
                </div>
              </div>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.imported_fill}
                  onChange={(e) => setFormData(prev => ({ ...prev, imported_fill: e.target.checked }))}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Imported fill required</span>
              </label>

              {/* Excavation Summary */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">Excavation Summary</h4>
                <div className="grid grid-cols-3 gap-4 text-center text-sm">
                  <div>
                    <p className="text-gray-600">Total Volume</p>
                    <p className="font-bold text-blue-700">{getExcavationVolume().toLocaleString()} m³</p>
                  </div>
                  <div>
                    <p className="text-gray-600">On-site Reuse</p>
                    <p className="font-bold text-green-700">{(getExcavationVolume() * formData.onsite_reuse_percentage / 100).toLocaleString()} m³</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Exported</p>
                    <p className="font-bold text-amber-700">{(getExcavationVolume() * (100 - formData.onsite_reuse_percentage) / 100).toLocaleString()} m³</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Rate Override (Advanced) */}
        <div className="border-t pt-4">
          <details className="text-sm">
            <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
              Advanced: Override waste generation rate
            </summary>
            <div className="mt-3 pl-4">
              <label className="block text-xs text-gray-500 mb-1">
                Custom rate (m³ per m² GFA) - Leave blank to use default
              </label>
              <input
                type="number"
                step="0.01"
                placeholder={`Default: ${constructionTypes.find(t => t.value === formData.construction_type)?.rate || 0.12}`}
                value={formData.construction_rate_override}
                onChange={(e) => setFormData(prev => ({ ...prev, construction_rate_override: e.target.value }))}
                className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                min="0.01"
                max="0.5"
              />
            </div>
          </details>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-6 border-t">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save & Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DCWMPStep4ConstructionPhase;
